/**
 * Shared `{match, conditions:[{field, op, value}]}` row-filter builder.
 *
 * The SAME always-AND filter shape is authored in two places — the dataset
 * row-filter (`DatasetFilterDialog`, ADR-0028 N3) and a declared metric's optional
 * WHERE (`MetricDialog`, ADR-0029). Extracting the op vocabulary + the value
 * coercion + the presentational rows keeps both in lockstep with the backend
 * `_ALL_OPS` / `validate_filter_spec` (one place to mirror).
 */
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type {
  DatasetFilterCondition,
  DatasetFilterSpec,
} from "@/lib/api-datasets";

/** Operators — mirrors the backend `_ALL_OPS` (hub.ask.query). */
export const FILTER_OPS = [
  "eq", "ne", "gt", "gte", "lt", "lte",
  "contains", "starts_with", "ends_with",
  "in", "not_in", "between",
  "is_null", "is_not_null",
] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

export const NO_VALUE: ReadonlySet<string> = new Set(["is_null", "is_not_null"]);
export const MULTI_VALUE: ReadonlySet<string> = new Set(["in", "not_in", "between"]);

const selectClass =
  "rounded-md border border-border bg-surface px-2 py-1.5 text-sm";

/** Editable condition row — `value` is always a raw string in the UI
 *  (comma-separated for in/not_in/between), coerced on save. */
export interface ConditionRow {
  field: string;
  op: FilterOp;
  value: string;
}

export function specToRows(spec: DatasetFilterSpec | undefined | null): ConditionRow[] {
  if (!spec?.conditions?.length) return [];
  return spec.conditions.map((c) => ({
    field: c.field,
    op: (FILTER_OPS as readonly string[]).includes(c.op) ? (c.op as FilterOp) : "eq",
    value: Array.isArray(c.value) ? c.value.join(", ") : c.value == null ? "" : String(c.value),
  }));
}

export function coerceValue(op: FilterOp, raw: string): DatasetFilterCondition["value"] {
  if (NO_VALUE.has(op)) return undefined;
  if (MULTI_VALUE.has(op)) {
    return raw.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
  }
  return raw;
}

/** Build the wire spec from UI rows, or `null` when there are no rows (clear). */
export function rowsToSpec(
  match: "all" | "any",
  rows: ConditionRow[],
): DatasetFilterSpec | null {
  if (rows.length === 0) return null;
  return {
    match,
    conditions: rows.map((r) => ({ field: r.field, op: r.op, value: coerceValue(r.op, r.value) })),
  };
}

/** A row is incomplete if it has no field or a value-taking op with an empty value. */
export function rowsIncomplete(rows: ConditionRow[]): boolean {
  return rows.some((r) => !r.field || (!NO_VALUE.has(r.op) && r.value.trim() === ""));
}

/**
 * Presentational condition-rows editor (match toggle + field·op·value rows).
 * Controlled: the parent owns `rows`/`match` state and the spec coercion.
 * `fields` is the live column list (a field outside it stays selectable for drift).
 */
export function ConditionRows({
  fields,
  rows,
  match,
  onRowsChange,
  onMatchChange,
  emptyHint,
}: {
  fields: { name: string }[];
  rows: ConditionRow[];
  match: "all" | "any";
  onRowsChange: (rows: ConditionRow[]) => void;
  onMatchChange: (m: "all" | "any") => void;
  emptyHint?: string;
}) {
  const { t } = useTranslation();

  const addRow = () =>
    onRowsChange([...rows, { field: fields[0]?.name ?? "", op: "eq", value: "" }]);
  const patchRow = (i: number, patch: Partial<ConditionRow>) =>
    onRowsChange(rows.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeRow = (i: number) => onRowsChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {rows.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("datasets.filterMatch", "Match")}</span>
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {(["all", "any"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onMatchChange(m)}
                className={
                  "px-2.5 py-1 text-xs transition " +
                  (match === m ? "bg-accent/15 text-foreground" : "text-muted-foreground hover:text-foreground")
                }
              >
                {m === "all" ? t("datasets.filterAll", "All (AND)") : t("datasets.filterAny", "Any (OR)")}
              </button>
            ))}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
          {emptyHint ?? t("datasets.filterNone", "No filter — add a condition to restrict it.")}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select
                aria-label={t("datasets.filterField", "Field")}
                value={row.field}
                className={selectClass}
                onChange={(e) => patchRow(i, { field: e.target.value })}
              >
                {/* Keep an out-of-list field selectable (schema drift). */}
                {!fields.some((f) => f.name === row.field) && row.field && (
                  <option value={row.field}>{row.field}</option>
                )}
                {fields.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.name}
                  </option>
                ))}
              </select>
              <select
                aria-label={t("datasets.filterOp", "Operator")}
                value={row.op}
                className={selectClass}
                onChange={(e) => patchRow(i, { op: e.target.value as FilterOp })}
              >
                {FILTER_OPS.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
              {!NO_VALUE.has(row.op) && (
                <Input
                  aria-label={t("datasets.filterValue", "Value")}
                  value={row.value}
                  placeholder={
                    MULTI_VALUE.has(row.op)
                      ? t("datasets.filterCsv", "comma, separated")
                      : t("datasets.filterValue", "Value")
                  }
                  className="w-40"
                  onChange={(e) => patchRow(i, { value: e.target.value })}
                />
              )}
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("common.delete", "Delete")}
                onClick={() => removeRow(i)}
              >
                <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button variant="outline" size="sm" onClick={addRow}>
        <Plus className="h-4 w-4" strokeWidth={1.5} />
        {t("datasets.filterAddCondition", "Add condition")}
      </Button>
    </div>
  );
}
